import { LanguageLevelObject, LanguageObject } from '@/components/Types';
import { US, DE, ES, RU, FR, CN, GB, AR, SA, FlagComponent } from 'country-flag-icons/react/3x2';

export const LANGUAGES = {
  spanish: { code: 'ES', label: 'Español', name: 'spanish', country: 'Spain', flag: ES, flagCode: 'ES', experimental: false, emoji: '🇪🇸', xiiCode: 'es', xiiCode2: 'spa', examName: 'DELE', extraLangDescriptor: 'European Spanish (Castellano)' } as LanguageObject,
  german: { code: 'DE', label: 'Deutsch', name: 'german', country: 'Germany', flag: DE, flagCode: 'DE', experimental: false, emoji: '🇩🇪', xiiCode: 'de', xiiCode2: 'deu', examName: 'TestDaF', extraLangDescriptor: 'German (Deutsch)' } as LanguageObject,    
  english: { code: 'EN-US', label: 'English', name: 'english', country: 'United Kingdom', flag: GB, flagCode: 'GB', experimental: false, emoji: '🇺🇸', xiiCode: 'en', xiiCode2: 'eng', examName: 'IELTS', extraLangDescriptor: 'English (UK)' } as LanguageObject,  
  french: { code: 'FR', label: 'Français', name: 'french', country: 'France', flag: FR, flagCode: 'FR', experimental: false, emoji: '🇫🇷', xiiCode: 'fr', xiiCode2: 'fra', examName: 'DELF', extraLangDescriptor: 'French' } as LanguageObject,  
  chinese: { code: 'CN', label: '汉语', name: 'chinese', country: 'China', flag: CN, flagCode: 'CN', experimental: true, emoji: '🇨🇳', xiiCode: 'zh', xiiCode2: 'cnh', examName: 'HSK', extraLangDescriptor: 'Mandarin Chinese' } as LanguageObject,  
} as const;

export const LEARNABLE_LANGUAGES = LANGUAGES;

export const DISABLED_LANGUAGES: (keyof typeof LANGUAGES)[] = [];

export const NATIVE_LANGUAGES = {
  ...LANGUAGES,
  russian: { code: 'RU', label: 'Русский', name: 'russian', country: 'Russia', flag: RU, flagCode: 'RU', experimental: false, emoji: '🇷🇺', xiiCode: 'ru', xiiCode2: 'rus', examName: '', extraLangDescriptor: null } as LanguageObject, 
  arabic: { code: 'SA', label: 'العربية', name: 'arabic', country: 'Saudi Arabia', flag: SA, flagCode: 'SA', experimental: false, emoji: '🇸🇦', xiiCode: 'ar', xiiCode2: 'ara', examName: '', extraLangDescriptor: null } as LanguageObject,
};

export const UI_LOCALE_LANGUAGES = {
  english: {...LANGUAGES.english},
  russian: {...NATIVE_LANGUAGES.russian},
  spanish: {...LANGUAGES.spanish},
  arabic: {...NATIVE_LANGUAGES.arabic},
} as const;

export function getLearnableLanguageFlag(language: string): FlagComponent | null {
  if (!language) return null;

  return LANGUAGES[language as keyof typeof LANGUAGES].flag;
}

export function getLearnableLanguageEmoji(language: string) {
  if (!language) return '';

  return LANGUAGES[language as keyof typeof LANGUAGES].emoji;
}

export function getLocaleLanguageFlag(language: string): FlagComponent | null {
  if (!language) return null;

  return UI_LOCALE_LANGUAGES[language as keyof typeof UI_LOCALE_LANGUAGES].flag;
}

export function getLocaleLanguageEmoji(language: string): string {
  if (!language) return '';

  return UI_LOCALE_LANGUAGES[language as keyof typeof UI_LOCALE_LANGUAGES].emoji;
}

export function getNativeLanguageByCode(code: string): LanguageObject | null {
  if (code == 'en') { // Special case for english, since we have just one native English locale
    code = 'en-us';
  }

  return Object.values(NATIVE_LANGUAGES).find((language) => language.code.toLowerCase() === code.toLowerCase()) || null;
}

export function getLanguageObjectByName(name: string): LanguageObject | null {
  return Object.values(NATIVE_LANGUAGES).find((language) => language.name.toLowerCase() === name.toLowerCase()) || null;
}

export const LANGUAGE_LEVELS = {
  beginner: { id: 'beginner', level: 1, label: 'Beginner A2', code: 'A2' },
  beginnerIntermediate: { id: 'beginnerIntermediate', level: 2, label: 'Beginner-Intermediate B1', code: 'B1' },
  intermediate: { id: 'intermediate', level: 3, label: 'Intermediate B2', code: 'B2' },
  advanced: { id: 'advanced', level: 4, label: 'Advanced C1+', code: 'C1+' },
} as const;

export function getLanguageLevelByLevel(level: number): LanguageLevelObject | null {
  return Object.values(LANGUAGE_LEVELS).find((languageLevel) => languageLevel.level === level) || null;
}

// Returns true if there's content for feed and story-based learning for a language pair
export function hasFeedCourse(language: string, nativeLanguage: string): boolean {
  if (language === 'spanish' && ['english', 'russian'].includes(nativeLanguage)) {
    return true;
  } else if (language === 'german' && ['english'].includes(nativeLanguage)) {
    return true;
  }

  return false;
}

// Availability of tutors for different language levels for each language
export const LANGUAGE_LEVELS_AVAILABILITY = {
  spanish: {
    beginner: true,
    beginnerIntermediate: true,
    intermediate: false,
    advanced: false,
  },
  german: {
    beginner: true,
    beginnerIntermediate: true,
    intermediate: false,
    advanced: false,
  },
  french: {
    beginner: true,
    beginnerIntermediate: false,
    intermediate: false,
    advanced: false,
  },
  chinese: {
    beginner: false,
    beginnerIntermediate: false,
    intermediate: false,
    advanced: false,
  },
  english: {
    beginner: true,
    beginnerIntermediate: true,
    intermediate: false,
    advanced: true,
  },
} as const;

export interface LanguageLevelMetrics {
  wpm: {min: number, max: number};
}

export type LanguageMetrics = Record<keyof typeof LANGUAGE_LEVELS, LanguageLevelMetrics>;

export const LANGUAGE_LEVEL_METRICS = {
  spanish: {
    beginner: {
      wpm: {min: 60, max: 120},
    } as LanguageLevelMetrics,
    beginnerIntermediate: {
      wpm: {min: 80, max: 140},
    } as LanguageLevelMetrics,
    intermediate: {
      wpm: {min: 80, max: 160},
    } as LanguageLevelMetrics,
    advanced: {
      wpm: {min: 120, max: 180},
    } as LanguageLevelMetrics,
  } as LanguageMetrics,  
  german: {
    beginner: {
      wpm: {min: 60, max: 120},
    } as LanguageLevelMetrics,
    beginnerIntermediate: {
      wpm: {min: 80, max: 140},
    } as LanguageLevelMetrics,
    intermediate: {
      wpm: {min: 80, max: 160},
    } as LanguageLevelMetrics,
    advanced: {
      wpm: {min: 120, max: 180},
    } as LanguageLevelMetrics,
  } as LanguageMetrics,
  french: {
    beginner: {
      wpm: {min: 60, max: 120},
    } as LanguageLevelMetrics,
    beginnerIntermediate: {
      wpm: {min: 80, max: 140},
    } as LanguageLevelMetrics,
    intermediate: {
      wpm: {min: 100, max: 140},
    } as LanguageLevelMetrics,
    advanced: {
      wpm: {min: 130, max: 200},
    } as LanguageLevelMetrics,
  },
  english: {
    beginner: {
      wpm: {min: 60, max: 90},
    } as LanguageLevelMetrics,
    beginnerIntermediate: {
      wpm: {min: 80, max: 120},
    } as LanguageLevelMetrics,
    intermediate: {
      wpm: {min: 100, max: 140},
    } as LanguageLevelMetrics,
    advanced: {
      wpm: {min: 130, max: 200},
    } as LanguageLevelMetrics,
  },
  chinese: {},
} as const;

export const USER_STARTING_CREDITS = 77;
export const USER_STARTING_CHAT_REPSONSES = 12;
export const USER_REFRESH_CHAT_REPSONSES = 12;

export const XP_REASONS = {
  practiceSession: {
    success: 'practice-session.success',
    noCorrections: 'practice-session.no-corrections',
    vocabUsed: 'practice-session.vocab-used',
    exactResponseUsed: 'practice-session.response-used',
  },
  examSession: {
    success: 'exam-session.success',
  },
  vocab: {
    wordLearned: 'vocab.word-learned',
  },
};

export const XP_POINTS = {
  practiceSession: {
    success: 5,
    noCorrections: 5,
    vocabUsed: 1,
    exactResponseUsed: 1,
  },
  examSession: {
    success: 15,
  },
  vocab: {
    wordLearned: 0,
  },
  feed: {
    articleSlideViewed: 1,
    quizQuestionAnswered: 0,
    quizQuestionAnsweredCorrectly: 3,
    audioComprehensionPlayed: 1,
    audioComprehensionPlayedTillEnd: 3,
  },
  todo: {
    todoCompleted: 5
  },
  forceAudioCheckThreshold: 1,
}

export const CREDIT_COST = {
  practiceSession: 10,
  examSession: 21,
  examSessionStage: 7,
  chatTokensStandard: 2,
  chatTokensPremium: 6,
}

export const USAGE_LIMITS = {
  chat: {
    free: {
      dailyOutputCharacters: 10000,
      dailyInputCharacters: 6000,
    }
  }
}

export const REFERRAL_REWARDS = {
  referredOnboarded: 50, // Credits given to a user who was referred by someone and completed onboarding
  referralOnboarded: 50, // Credits given to a user who referred someone and the referred user completed onboarding
}

export interface CreditPack {
  sizeName: 'xxs' | 'xs' | 's' | 'm' | 'l' | 'xl';
  credits: number;
  price: number;
  currency: string;
  stripeProductId?: string | null;
  stripeLink?: string | null;
  stripeLinkId?: string | null;
}

export const CREDIT_PACKS: CreditPack[] = [
  {
    sizeName: 'xxs',
    credits: 5,
    price: 1.99,
    currency: 'eur',
    stripeProductId: 'prod_SFxf65Xv8WAaUS',
    stripeLink: "https://buy.stripe.com/9AQ6oYfq71JibW83ce",
    stripeLinkId: 'plink_1RLkaOI48L2whZcZKpejbI1Y'
  },
  {
    sizeName: 'xs',
    credits: 50,
    price: 9.99,
    currency: 'eur',
    stripeProductId: 'prod_SGGxU45LOV9UO2',
    stripeLink: "https://buy.stripe.com/eVa9Ba0vd9bKgcoeUX",
    stripeLinkId: 'plink_1RLkafI48L2whZcZlKhDVNIW'
  },
  {
    sizeName: 's',
    credits: 100,
    price: 14.99,
    currency: 'eur',
    stripeProductId: 'prod_SFxeiB6VRbOFaH',
    stripeLink: "https://buy.stripe.com/4gw7t2fq7afO2ly5ko",
    stripeLinkId: 'plink_1RLkarI48L2whZcZIAb5UmeC'
  },
  {
    sizeName: 'm',
    credits: 150,
    price: 19.99,
    currency: 'eur',
    stripeProductId: 'prod_SGH2psCeasRUBa',
    stripeLink: "https://buy.stripe.com/9AQeVu91J73C7FS5kp",
    stripeLinkId: 'plink_1RLkb5I48L2whZcZYPXjyBPY'
  },
  {
    sizeName: 'l',
    credits: 220,
    price: 24.99,
    currency: 'eur',
    stripeProductId: 'prod_SGH4wTwLbk9Cqn',
    stripeLink: "https://buy.stripe.com/6oEeVu91J73C2ly4gm",
    stripeLinkId: 'plink_1RLkbFI48L2whZcZmNBDQNXm'
  },  
];


export const EXAM_STAGES = {
  monologue: 'monologue',
  dialogue: 'dialogue',
  describing: 'describing',
} as const;

export const FIRST_EXAM_STAGE = 'monologue';

export const EXAM_STAGES_ORDER = [
  EXAM_STAGES.monologue,
  EXAM_STAGES.dialogue,
  EXAM_STAGES.describing,
] as const;

export const EXAM_TYPES = {
  spanish: {
    oral: 'oral'
  },
  german: {
    oral: 'oral'
  },
  french: {
    oral: 'oral'
  },
  english: {
    oral: 'oral'
  },
  chinese: {
    oral: 'oral'
  },
} as const;

export const EXAM_NAMES: Record<keyof typeof EXAM_TYPES, Record<keyof typeof EXAM_TYPES[keyof typeof EXAM_TYPES], Record<keyof typeof LANGUAGE_LEVELS, string>>> = {
  spanish: {
    oral: {
      beginner: 'DELE A2',
      beginnerIntermediate: 'DELE B1',
      intermediate: 'DELE B2',
      advanced: 'DELE C1',
    }
  },
  german: {
    oral: {
      beginner: 'TestDaF B1',
      beginnerIntermediate: 'TestDaF B2',
      intermediate: 'TestDaF B2',
      advanced: 'TestDaF C1',
    }
  },
  french: {
    oral: {
      beginner: 'DELF A1',
      beginnerIntermediate: 'DELF B1',
      intermediate: 'DELF B2',
      advanced: 'DELF C1',
    }
  },
  english: {
    oral: {
      beginner: 'IELTS 4',
      beginnerIntermediate: 'IELTS 5',
      intermediate: 'IELTS 6',
      advanced: 'IELTS 7',
    }
  },
  chinese: {
    oral: {
      beginner: 'HSK 1',
      beginnerIntermediate: 'HSK 2',
      intermediate: 'HSK 3',
      advanced: 'HSK 4',
    }
  },
} as const;

export const MAX_USER_GOAL_LENGTH = 100;

export const PRACTICE_TURN_TIME_SECONDS = 17;
export const EXAM_PREPARE_TIME_SECONDS = 600;

export const PRACTICE_TURN_TIME_SECONDS_PER_LEVEL = {
  beginner: 18,
  beginnerIntermediate: 15,
  intermediate: 15,
  advanced: 25,
}

export const EXAM_MONOLOGUE_TIME_SECONDS = {
  beginner: 240,
  beginnerIntermediate: 320,
  intermediate: 420,
  advanced: 420,
}

export const EXAM_DIALOGUE_TIME_SECONDS = { 
  beginner: 120,
  beginnerIntermediate: 120,
  intermediate: 120,
  advanced: 120,
}

export const EXAM_DESCRIBING_TIME_SECONDS = {
  beginner: 120,
  beginnerIntermediate: 120,
  intermediate: 120,
  advanced: 120,
}

export const SILENCE_AUDIO_URL = "/speech/silence.mp3";

export const CHAT_MESSAGE_VISIBLE_ROLES = ['user', 'assistant', 'notice'];

// These values are used to track certain events that may trigger a todo task completion
export const TODO_TASK_COMPLETION_ACTIONS = {
  practiceCompleted: 'practiceCompleted',
  voiceChatCompleted: 'voiceChatCompleted',
  fiveMessagesSentInPractice: 'fiveMessagesSentInPractice',
  fiveMessagesSentInVoiceChat: 'fiveMessagesSentInVoiceChat',
}

export const FEED_REACTIONS = ["👍", "😂", "😮", "🤨", "🤯"];

export const MAX_GAME_LIVES = 10;

export const DEFAULT_TODO_LIST_TAG = 'default';